import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MaterialModule } from '@fantaz/shared/ui/material';
import { formatDate } from '@fantaz/shared/utils/date';

@Component({
  selector: 'fz-verification-failure-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './verification-dialog.component.html',
  styleUrls: ['./verification-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerificationFailureDialogComponent implements OnInit, OnDestroy {
  /**
   * Allowed options to select
   */
  allowedOptions = {
    editPersonalInfo: 'edit-personal-info',
    registerCreditCard: 'register-credit-card',
    uploadPicture: 'upload-picture',
    tryAnotherMethod: 'try-another-method',
  };

  selectedOption = this.allowedOptions.registerCreditCard;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public dialogRef: MatDialogRef<VerificationFailureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  get attempts(): any | null {
    if (this.data?.attempts) {
      return this.data.attempts;
    }

    return null;
  }

  get remainAttempts(): number | undefined {
    if (this?.attempts?.current?.CUSTOMER_IDENTITY >= 0) {
      return (
        this.data.attempts.max.CUSTOMER_IDENTITY -
        this.data.attempts.current.CUSTOMER_IDENTITY
      );
    }

    return undefined;
  }

  get isDisabledCreditCardOptions(): boolean {
    const attempts = this.attempts;
    if (attempts?.current?.CC >= 0 && attempts?.max.CC >= 0) {
      return attempts.current.CC >= attempts.max.CC;
    }
    return false;
  }

  get isDisabledUploadOptions(): boolean {
    const attempts = this.attempts;
    if (attempts?.current?.DOCUMENT >= 0 && attempts?.max.DOCUMENT >= 0) {
      return attempts.current.DOCUMENT >= attempts.max.DOCUMENT;
    }
    return false;
  }

  get title(): string {
    if (this.remainAttempts && this.remainAttempts > 0) {
      return "Sorry, we couldn't verify your personal info";
    }

    return 'Personal info verification attempts exceeded';
  }

  get content(): string {
    if (this.remainAttempts && this.remainAttempts > 0) {
      return `Don't worry, you still have <b>${this.remainAttempts} attempt</b> to verify your player profile by editing your info, or you can try another verification method.`;
    }

    return `Your attempts to verify your personal info have been unsuccessful and you have reached the maximum number of attempts. Don't worry, you can still continue with your player profile verification by registering a credit card or uploading your ID picture.`;
  }

  ngOnInit(): void {
    if (this.isDisabledCreditCardOptions) {
      this.selectedOption = this.allowedOptions.uploadPicture;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: 'verification-failure' },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { m: null },
      replaceUrl: true,
      queryParamsHandling: 'merge',
    });
  }

  selectOption(option: string) {
    this.dialogRef.close({
      signal: option,
    });
  }

  formatBirthday(date: string): string {
    if (date) {
      return formatDate(date);
    }
    return '';
  }

  handleSelection() {
    this.selectOption(this.selectedOption);
  }

  handleClose() {
    this.dialogRef.close({
      signal: 'close-button',
    });
  }
}
